import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { CreditCard, Plus, X, ExternalLink, DollarSign, Trash2 } from 'lucide-react';
import { API } from './constants';

const STATUS_STYLES = {
  pending:   'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
  paid:      'bg-green-500/10 text-green-400 border-green-500/30',
  cancelled: 'bg-white/10 text-white/50 border-white/20',
  refunded:  'bg-orange-500/10 text-orange-400 border-orange-500/30',
  failed:    'bg-red-500/10 text-red-400 border-red-500/30',
  expired:   'bg-white/10 text-white/50 border-white/20',
};

const currency = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const PaymentsTab = () => {
  const [invoices, setInvoices] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [clients, setClients] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [invRes, txRes, clientsRes, pkgRes] = await Promise.all([
        axios.get(`${API}/admin/invoices`, { withCredentials: true }),
        axios.get(`${API}/admin/payments`, { withCredentials: true }),
        axios.get(`${API}/crm/clients`, { withCredentials: true }),
        axios.get(`${API}/payments/packages`),
      ]);
      setInvoices(invRes.data);
      setTransactions(txRes.data);
      setClients(clientsRes.data);
      setPackages(pkgRes.data.packages || []);
    } catch (e) {
      toast.error('Failed to load payments data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCancel = async (invId) => {
    if (!window.confirm('Cancel this pending invoice? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/invoices/${invId}`, { withCredentials: true });
      toast.success('Invoice cancelled');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to cancel invoice');
    }
  };

  const totals = invoices.reduce((acc, inv) => {
    if (inv.status === 'paid')    acc.paid    += Number(inv.amount || 0);
    if (inv.status === 'pending') acc.pending += Number(inv.amount || 0);
    return acc;
  }, { paid: 0, pending: 0 });

  return (
    <div data-testid="admin-payments">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-heading text-2xl text-white">Payments &amp; Invoices</h2>
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-2 px-5 py-3 bg-[#D4AF37] text-black text-xs uppercase tracking-widest hover:bg-[#C9A432] transition-colors"
          data-testid="new-invoice-btn"
        >
          <Plus size={16} /> New Invoice
        </button>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        <StatCard label="Collected" value={currency(totals.paid)} icon={DollarSign} accent="text-green-400" />
        <StatCard label="Outstanding" value={currency(totals.pending)} icon={CreditCard} accent="text-yellow-400" />
        <StatCard label="Invoices" value={invoices.length} icon={CreditCard} accent="text-[#D4AF37]" />
        <StatCard label="Transactions" value={transactions.length} icon={CreditCard} accent="text-white" />
      </div>

      {/* Invoices table */}
      <div className="bg-[#0a0a0a] border border-white/10 mb-10">
        <div className="px-6 py-4 border-b border-white/10">
          <h3 className="font-heading text-lg text-white">Invoices</h3>
        </div>
        {loading ? (
          <div className="p-8 text-white/40 text-sm">Loading…</div>
        ) : invoices.length === 0 ? (
          <div className="p-8 text-white/40 text-sm">No invoices yet. Click &ldquo;New Invoice&rdquo; to create one.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-white/40 text-[10px] uppercase tracking-widest border-b border-white/5">
                  <th className="px-6 py-3">Client</th>
                  <th className="px-6 py-3">Invoice</th>
                  <th className="px-6 py-3">Kind</th>
                  <th className="px-6 py-3 text-right">Amount</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Created</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.invoice_id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`invoice-row-${inv.invoice_id}`}>
                    <td className="px-6 py-4 text-white">{inv.client_name || '—'}<div className="text-white/40 text-xs">{inv.client_email}</div></td>
                    <td className="px-6 py-4 text-white/80">{inv.title}</td>
                    <td className="px-6 py-4 text-white/60">{inv.kind.replace(/_/g, ' ')}</td>
                    <td className="px-6 py-4 text-right text-[#D4AF37] font-mono">{currency(inv.amount)}</td>
                    <td className="px-6 py-4">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${STATUS_STYLES[inv.status] || 'bg-white/10 text-white/60 border-white/20'}`}>
                        {inv.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-white/40 text-xs">{inv.created_at ? format(new Date(inv.created_at), 'MMM d, yyyy') : '—'}</td>
                    <td className="px-6 py-4 text-right">
                      {inv.status === 'pending' && (
                        <button
                          onClick={() => handleCancel(inv.invoice_id)}
                          className="text-white/40 hover:text-red-400 transition-colors"
                          data-testid={`cancel-invoice-${inv.invoice_id}`}
                          title="Cancel invoice"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Transactions table */}
      <div className="bg-[#0a0a0a] border border-white/10">
        <div className="px-6 py-4 border-b border-white/10">
          <h3 className="font-heading text-lg text-white">Stripe Transactions</h3>
          <p className="text-white/40 text-xs mt-1">Raw checkout sessions — includes pending, abandoned, and completed.</p>
        </div>
        {transactions.length === 0 ? (
          <div className="p-8 text-white/40 text-sm">No transactions yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-white/40 text-[10px] uppercase tracking-widest border-b border-white/5">
                  <th className="px-6 py-3">Session</th>
                  <th className="px-6 py-3 text-right">Amount</th>
                  <th className="px-6 py-3">Payment Status</th>
                  <th className="px-6 py-3">Created</th>
                  <th className="px-6 py-3">Stripe</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => (
                  <tr key={tx.session_id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`tx-row-${tx.session_id}`}>
                    <td className="px-6 py-4 text-white/70 font-mono text-xs">{tx.session_id?.slice(0, 20)}…</td>
                    <td className="px-6 py-4 text-right text-[#D4AF37] font-mono">{currency(tx.amount)}</td>
                    <td className="px-6 py-4">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${STATUS_STYLES[tx.payment_status] || 'bg-white/10 text-white/60 border-white/20'}`}>
                        {tx.payment_status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-white/40 text-xs">{tx.created_at ? format(new Date(tx.created_at), 'MMM d, h:mm a') : '—'}</td>
                    <td className="px-6 py-4">
                      <a
                        href={`https://dashboard.stripe.com/test/payments/${tx.stripe_payment_intent_id || tx.session_id}`}
                        target="_blank" rel="noreferrer"
                        className="text-white/50 hover:text-[#D4AF37] inline-flex items-center gap-1 text-xs"
                      >
                        <ExternalLink size={12} /> View
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <NewInvoiceModal
          clients={clients}
          packages={packages}
          onClose={() => setShowModal(false)}
          onCreated={() => { setShowModal(false); fetchAll(); }}
        />
      )}
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, accent }) => (
  <div className="bg-[#0a0a0a] border border-white/10 p-6">
    <Icon className={`w-7 h-7 ${accent} mb-4`} />
    <p className="text-2xl font-heading text-white">{value}</p>
    <p className="text-white/60 text-xs mt-1 uppercase tracking-widest">{label}</p>
  </div>
);

const NewInvoiceModal = ({ clients, packages, onClose, onCreated }) => {
  const [kind, setKind] = useState('package');
  const [crmClientId, setCrmClientId] = useState(clients[0]?.crm_id || '');
  const [packageKey, setPackageKey] = useState(packages[0]?.key || 'strategy_session');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!crmClientId) { toast.error('Select a CRM client'); return; }
    setSubmitting(true);
    const payload = { crm_client_id: crmClientId, kind };
    if (kind === 'package') {
      payload.package_key = packageKey;
      if (description) payload.description = description;
    } else {
      payload.title = title;
      payload.description = description || null;
      payload.amount = parseFloat(amount);
    }
    try {
      await axios.post(`${API}/admin/invoices`, payload, { withCredentials: true });
      toast.success(kind === 'package' ? 'Invoice created — pay link is in the client portal' : 'Invoice created');
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-lg p-8" data-testid="new-invoice-modal">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading text-xl text-white">New Invoice</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X size={20} /></button>
        </div>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="block text-white/40 text-[10px] uppercase tracking-widest mb-2">CRM Client</label>
            <select
              value={crmClientId}
              onChange={(e) => setCrmClientId(e.target.value)}
              className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37]"
              data-testid="invoice-client-select"
              required
            >
              <option value="">— Select a client —</option>
              {clients.map((c) => (
                <option key={c.crm_id} value={c.crm_id}>{c.name} — {c.email}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-white/40 text-[10px] uppercase tracking-widest mb-2">Invoice Type</label>
            <div className="flex gap-3">
              <label className={`flex-1 border px-4 py-3 cursor-pointer text-center text-xs uppercase tracking-widest ${kind === 'package' ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-white/10 text-white/60'}`}>
                <input type="radio" className="hidden" checked={kind === 'package'} onChange={() => setKind('package')} />
                Package
              </label>
              <label className={`flex-1 border px-4 py-3 cursor-pointer text-center text-xs uppercase tracking-widest ${kind === 'custom' ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-white/10 text-white/60'}`}>
                <input type="radio" className="hidden" checked={kind === 'custom'} onChange={() => setKind('custom')} />
                Custom
              </label>
            </div>
          </div>

          {kind === 'package' ? (
            <div>
              <label className="block text-white/40 text-[10px] uppercase tracking-widest mb-2">Package</label>
              <select
                value={packageKey}
                onChange={(e) => setPackageKey(e.target.value)}
                className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37]"
                data-testid="invoice-package-select"
              >
                {packages.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.title} — ${p.total.toLocaleString()}
                  </option>
                ))}
              </select>
              <p className="text-white/40 text-xs mt-2">
                Creates a single invoice at the package&rsquo;s full price. For <span className="text-[#D4AF37]">Elite Concierge (custom pricing)</span>, use the <span className="text-white">Custom</span> invoice type.
              </p>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-white/40 text-[10px] uppercase tracking-widest mb-2">Title</label>
                <input
                  type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                  className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37]"
                  placeholder="e.g. Retainer — May 2026"
                  required
                  data-testid="invoice-title-input"
                />
              </div>
              <div>
                <label className="block text-white/40 text-[10px] uppercase tracking-widest mb-2">Amount (USD)</label>
                <input
                  type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                  className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37]"
                  placeholder="1500.00"
                  required
                  data-testid="invoice-amount-input"
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-white/40 text-[10px] uppercase tracking-widest mb-2">Description (optional)</label>
            <textarea
              value={description} onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37]"
              data-testid="invoice-description-input"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button" onClick={onClose}
              className="flex-1 border border-white/10 text-white py-3 text-xs uppercase tracking-widest hover:border-white/30"
            >Cancel</button>
            <button
              type="submit" disabled={submitting}
              className="flex-1 bg-[#D4AF37] text-black py-3 text-xs uppercase tracking-widest hover:bg-[#C9A432] disabled:opacity-50"
              data-testid="invoice-submit-btn"
            >
              {submitting ? 'Creating…' : 'Create Invoice'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
